import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../../utils/ThemeSwitcher';
import { X } from 'lucide-react';

// Responsive hook for mobile detection
const useResponsive = () => {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1200,
    height: typeof window !== 'undefined' ? window.innerHeight : 800,
  });
  
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  useEffect(() => {
    const checkTouch = () => {
      setIsTouchDevice(
        'ontouchstart' in window || 
        navigator.maxTouchPoints > 0 ||
        window.matchMedia('(pointer: coarse)').matches
      );
    };
    
    checkTouch();
    
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = windowSize.width < 900 || (isTouchDevice && windowSize.width < 1024);

  return { isMobile, isTouchDevice, width: windowSize.width, height: windowSize.height };
};


const ContentModal = ({ 
  isOpen, 
  onClose, 
  title,
  subtitle,
  icon: Icon,
  iconColor = '#4FC3F7',
  children,
  maxWidth = '99vw',
  minWidth = '99.5vw',
  maxHeight = '99vh',
  minHeight = '99vh',
  showCloseButton = true,
  closeOnBackdrop = true,
  closeOnEscape = true,
  hideHeader = false, // Option to hide modal header (for components with their own header)
}) => {
  const { isDarkMode } = useTheme();
  const { isMobile } = useResponsive();
  const [isClosing, setIsClosing] = useState(false);
  const [canScroll, setCanScroll] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartY = useRef(0);
  const modalRef = useRef(null);
  const contentRef = useRef(null);
  
  // Colors
  const colors = {
    overlay: isDarkMode ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.7)',
    modalBg: isDarkMode ? '#0D0E0E' : '#F5F5F5',
    headerBg: isDarkMode ? '#111114' : '#FFFFFF',
    text: isDarkMode ? '#E5E7EB' : '#1F2937',
    textSecondary: isDarkMode ? '#9CA3AF' : '#6B7280',
    textMuted: isDarkMode ? '#6B7280' : '#9CA3AF',
  };
  
  // Font stack matching the app's design system
  const fontFamily = "'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
  
  // Handle close with animation
  const handleClose = () => {
    if (!onClose) return;
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 200);
  };

  // Close on escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen && closeOnEscape) {
        handleClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, closeOnEscape]);
  
  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && closeOnBackdrop) {
      handleClose();
    }
  };

  // Mobile swipe-to-close gesture handlers
  const handleDragStart = (e) => {
    if (!isMobile) return;
    const touch = e.touches[0];
    dragStartY.current = touch.clientY;
    setIsDragging(true);
  };

  const handleDragMove = (e) => {
    if (!isMobile || !isDragging) return;
    const touch = e.touches[0];
    const delta = touch.clientY - dragStartY.current;
    // Only allow dragging downward
    if (delta > 0) {
      setDragOffset(delta);
      e.preventDefault();
    }
  };

  const handleDragEnd = () => {
    if (!isMobile || !isDragging) return;
    setIsDragging(false);
    // Close if dragged more than 80px down
    if (dragOffset > 80) {
      handleClose();
    }
    setDragOffset(0);
  };

  // Detect whether content overflows and track scroll position
  useEffect(() => {
    const el = contentRef.current;
    if (!el || !isOpen) return;

    const checkScroll = () => {
      const hasOverflow = el.scrollHeight > el.clientHeight + 2;
      setCanScroll(hasOverflow);
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 8;
      setIsAtBottom(atBottom);
    };

    // Check on mount and after content renders
    checkScroll();
    const timer = setTimeout(checkScroll, 100);
    el.addEventListener('scroll', checkScroll, { passive: true });

    // ResizeObserver for dynamic content changes
    const ro = new ResizeObserver(checkScroll);
    ro.observe(el);

    return () => {
      clearTimeout(timer);
      el.removeEventListener('scroll', checkScroll);
      ro.disconnect();
    };
  }, [isOpen, children]);
  
  if (!isOpen) return null;
  
  // Determine if we should show the header
  const shouldShowHeader = !hideHeader && (title || (showCloseButton && !hideHeader));
  
  // Responsive sizing
  const responsiveMaxWidth = isMobile ? '100vw' : maxWidth;
  const responsiveMinWidth = isMobile ? '100vw' : minWidth;
  const responsiveMaxHeight = isMobile ? '93dvh' : maxHeight;
  const responsiveMinHeight = isMobile ? 'auto' : minHeight;
  const responsivePadding = isMobile ? '0' : '10px';
  const responsiveBorderRadius = isMobile ? '0' : '10px';
  
  return (
    <div
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.25)' : 'rgba(0, 0, 0, 0.08)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: isMobile ? 'flex-end' : 'center',
        justifyContent: 'center',
        zIndex: 9000,
        padding: responsivePadding,
        fontFamily: fontFamily,
        animation: isClosing ? 'modalFadeOut 0.2s ease-out forwards' : 'modalFadeIn 0.25s ease-out',
      }}
    >
      <div
        ref={modalRef}
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: responsiveMaxWidth,
          minWidth: responsiveMinWidth,
          maxHeight: responsiveMaxHeight,
          minHeight: responsiveMinHeight,
          height: isMobile ? '93dvh' : 'auto',
          backgroundColor: colors.modalBg,
          borderRadius: responsiveBorderRadius,
          borderTopLeftRadius: isMobile ? '16px' : responsiveBorderRadius,
          borderTopRightRadius: isMobile ? '16px' : responsiveBorderRadius,
          boxShadow: isDarkMode
            ? '0 25px 60px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255,255,255,0.06)' 
            : '0 25px 60px -12px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0,0,0,0.06)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: fontFamily,
          animation: isClosing 
            ? (isMobile ? 'modalSlideDown 0.2s ease-out forwards' : 'modalSlideOut 0.2s ease-out forwards')
            : (isMobile ? 'modalSlideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)' : 'modalSlideIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)'),
          ...(isMobile && dragOffset > 0 ? {
            transform: `translateY(${dragOffset}px)`,
            opacity: Math.max(0.5, 1 - (dragOffset / 400)),
            transition: isDragging ? 'none' : 'transform 0.3s ease, opacity 0.3s ease',
            animation: 'none',
          } : {}),
        }}
      >
        {/* Mobile drag handle — swipe down to close */}
        {isMobile && (
          <div 
            onTouchStart={handleDragStart}
            onTouchMove={handleDragMove}
            onTouchEnd={handleDragEnd}
            style={{
              width: '100%',
              padding: '14px 0 10px',
              display: 'flex',
              justifyContent: 'center',
              backgroundColor: colors.headerBg,
              cursor: 'grab',
              touchAction: 'none',
            }}
          >
            <div 
              className="dq-sheet-handle"
              style={{
              width: '48px',
              height: '5px',
              borderRadius: '3px',
              backgroundColor: isDragging 
                ? (isDarkMode ? '#6B7280' : '#9CA3AF') 
                : (isDarkMode ? '#3f3f46' : '#D1D5DB'),
              transition: 'background-color 0.15s ease',
            }} />
          </div>
        )}
        
        {/* Header - only show if not hidden and title provided */}
        {shouldShowHeader && (
          <div style={{
            padding: isMobile ? '12px 16px' : '10px 20px',
            backgroundColor: colors.headerBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '10px' : '10px' }}>
              {Icon && (
                <div style={{
                  width: isMobile ? '40px' : '34px',
                  height: isMobile ? '40px' : '34px',
                  borderRadius: '9px',
                  background: `linear-gradient(135deg, ${iconColor}20 0%, ${iconColor}10 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <Icon size={isMobile ? 20 : 17} color={iconColor} />
                </div>
              )}
              
              {title && (
                <div>
                  <h2 style={{ 
                    margin: 0,
                    fontSize: isMobile ? '18px' : '16px',
                    fontWeight: '600',
                    color: colors.text,
                    fontFamily: fontFamily,
                  }}>
                    {title}
                  </h2>
                  {subtitle && (
                    <p style={{
                      margin: '2px 0 0 0',
                      fontSize: isMobile ? '12px' : '11px',
                      color: colors.textSecondary,
                      fontFamily: fontFamily,
                    }}>
                      {subtitle}
                    </p>
                  )}
                </div>
              )}
            </div>
            
            {/* Close button in header — hidden on mobile (swipe-to-close instead) */}
            {showCloseButton && !isMobile && (
              <div
                role="button"
                tabIndex={0}
                onClick={handleClose}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClose(); } }}
                style={{
                  width: isMobile ? '44px' : '25px',
                  height: isMobile ? '44px' : '25px',
                  borderRadius: '1000px',
                  border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
                  backgroundColor: isDarkMode ? '#1f1f23' : '#F3F4F6',
                  color: colors.textSecondary,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                  fontFamily: fontFamily,
                  WebkitTapHighlightColor: 'transparent',
                  flexShrink: 0,
                  boxSizing: 'border-box',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = isDarkMode ? '#EF4444' : '#EF4444';
                  e.currentTarget.style.color = '#FFFFFF';
                  e.currentTarget.style.borderColor = '#EF4444';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = isDarkMode ? '#1f1f23' : '#F3F4F6';
                  e.currentTarget.style.color = colors.textSecondary;
                  e.currentTarget.style.borderColor = isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <X size={12} />
              </div>
            )}
          </div>
        )}
        
        {/* Floating close button when header is hidden — hidden on mobile (swipe-to-close instead) */}
        {hideHeader && showCloseButton && !isMobile && (
          <div
            role="button"
            tabIndex={0}
            onClick={handleClose}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClose(); } }}
            style={{
              position: 'absolute',
              top: isMobile ? '16px' : '6px',
              right: isMobile ? '16px' : '6px',
              width: isMobile ? '44px' : '25px',
              height: isMobile ? '44px' : '25px',
              borderRadius: '1200px',
              border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
              backgroundColor: isDarkMode ? 'rgba(15, 15, 18, 0.9)' : 'rgba(255, 255, 255, 0.9)',
              color: isDarkMode ? colors.textSecondary : colors.textSecondary,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
              zIndex: 1000,
              boxSizing: 'border-box',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              WebkitTapHighlightColor: 'transparent',
              boxShadow: isDarkMode 
                ? '0 4px 12px rgba(0,0,0,0.3)' 
                : '0 4px 12px rgba(0,0,0,0.08)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#FC5C58';
              e.currentTarget.style.color = '#820D10';
              e.currentTarget.style.borderColor = '#DF4845';
              e.currentTarget.style.transform = 'scale(1.08)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = isDarkMode ? 'rgba(15, 15, 18, 0.9)' : 'rgba(255, 255, 255, 0.9)';
              e.currentTarget.style.color = colors.textSecondary;
              e.currentTarget.style.borderColor = isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            <X size={12} />
          </div>
        )}
        
        {/* Content */}
        <div 
          ref={contentRef}
          style={{ 
          flex: 1, 
          overflow: 'auto',
          position: 'relative',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch',
        }}
        className="content-modal-scroll-container"
        >
          {children}
        </div>

        {/* Bottom scroll fade indicator — only when content overflows and not at bottom */}
        {canScroll && !isAtBottom && (
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '40px',
            background: `linear-gradient(transparent, ${colors.modalBg})`,
            pointerEvents: 'none',
            zIndex: 5,
            borderRadius: `0 0 ${responsiveBorderRadius} ${responsiveBorderRadius}`,
            transition: 'opacity 0.2s ease',
          }} />
        )}
      </div>
      
      {/* Animations */}
      <style>{`
        @keyframes modalFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes modalFadeOut {
          from { opacity: 1; }
          to { opacity: 0; }
        }
        
        @keyframes modalSlideIn {
          from { 
            opacity: 0; 
            transform: scale(0.96) translateY(10px); 
          }
          to { 
            opacity: 1; 
            transform: scale(1) translateY(0); 
          }
        }
        
        @keyframes modalSlideOut {
          from { 
            opacity: 1; 
            transform: scale(1) translateY(0); 
          }
          to { 
            opacity: 0; 
            transform: scale(0.96) translateY(10px); 
          }
        }
        
        /* Mobile-specific slide animations */
        @keyframes modalSlideUp {
          from { 
            opacity: 0; 
            transform: translateY(100%); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }
        
        @keyframes modalSlideDown {
          from { 
            opacity: 1; 
            transform: translateY(0); 
          }
          to { 
            opacity: 0; 
            transform: translateY(100%); 
          }
        }
        
        /* Hide scrollbars in content modal */
        .content-modal-scroll-container::-webkit-scrollbar {
          display: none !important;
          width: 0 !important;
          height: 0 !important;
        }
        
        .content-modal-scroll-container {
          scrollbar-width: none !important;
          -ms-overflow-style: none !important;
        }
        
        .content-modal-scroll-container *::-webkit-scrollbar {
          display: none !important;
          width: 0 !important;
          height: 0 !important;
        }
        
        .content-modal-scroll-container * {
          scrollbar-width: none !important;
          -ms-overflow-style: none !important;
        }
        
        @keyframes handleBounce {
          0%, 100% { transform: translateY(0); }
          40% { transform: translateY(6px); }
          60% { transform: translateY(3px); }
          80% { transform: translateY(5px); }
        }
        .dq-sheet-handle {
          animation: handleBounce 0.6s ease-out 0.35s 1;
        }
      `}</style>
    </div>
  );
};

export default ContentModal;